import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/DashboardLayout';
import { AddDeliverableModal } from '@/components/deliverables/AddDeliverableModal';
import { DeliverableStatusBadge } from '@/components/deliverables/DeliverableStatusBadge';
import { DeliverableCategoryBadge } from '@/components/deliverables/DeliverableCategoryBadge';
import { Deliverable, DeliverableStatus, DeliverableCategory } from '@/types/deliverables';
import { BulkDeliverableActions } from '@/components/BulkDeliverableActions';
import { DeliverableRevisionHistory } from '@/components/DeliverableRevisionHistory';
import { DeliverableDependencySelector } from '@/components/DeliverableDependencySelector';
import { DeliverableCommentThread } from '@/components/DeliverableCommentThread';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import {
  Package,
  Plus,
  Search,
  Filter,
  Download,
  Edit,
  Trash2,
  CheckCircle,
  ExternalLink,
  Mail,
  Calendar,
  User,
  TrendingUp,
  AlertTriangle,
  Clock,
  FileText,
  ChevronDown,
  ChevronUp,
  Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface FilterState {
  status: DeliverableStatus | 'ALL';
  category: DeliverableCategory | 'ALL';
  assignedTo: string;
  leadId: string;
  search: string;
}

export const DeliverablesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAutoCreateModal, setShowAutoCreateModal] = useState(false);
  const [autoCreateLeadId, setAutoCreateLeadId] = useState('');
  const [autoCreatePlanId, setAutoCreatePlanId] = useState('');
  const [editingDeliverable, setEditingDeliverable] = useState<Deliverable | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedTab, setExpandedTab] = useState<'revisions' | 'dependencies' | 'comments'>('comments');
  const ITEMS_PER_PAGE = 20;
  const [filters, setFilters] = useState<FilterState>({
    status: 'ALL',
    category: 'ALL',
    assignedTo: '',
    leadId: '',
    search: ''
  });

  // Fetch deliverables
  const { data: deliverables = [], isLoading, error: deliverablesError, refetch: refetchDeliverables } = useQuery<Deliverable[]>({
    queryKey: ['deliverables', filters],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (filters.status !== 'ALL') params.append('status', filters.status);
      if (filters.category !== 'ALL') params.append('category', filters.category);
      if (filters.assignedTo) params.append('assignedToId', filters.assignedTo);
      if (filters.leadId) params.append('leadId', filters.leadId);

      const response = await fetch(`${API_BASE_URL}/deliverables?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.status === 403) throw new Error('You don\'t have permission for this action');
      if (!response.ok) throw new Error('Failed to fetch deliverables');
      const result = await response.json();
      return result.data || [];
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/deliverables/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to delete deliverable');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast({
        title: 'Success',
        description: 'Deliverable deleted successfully'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Mark as delivered mutation
  const markDeliveredMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/deliverables/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'DELIVERED',
          deliveredDate: new Date().toISOString()
        })
      });
      if (!response.ok) throw new Error('Failed to update deliverable');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast({
        title: 'Success',
        description: 'Marked as delivered'
      });
    }
  });

  // Mark as verified mutation
  const markVerifiedMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/deliverables/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'VERIFIED'
        })
      });
      if (!response.ok) throw new Error('Failed to update deliverable');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast({
        title: 'Success',
        description: 'Marked as verified'
      });
    }
  });

  // Fetch WON leads for auto-create modal
  const { data: wonLeads = [] } = useQuery<{ id: string; name: string; company: string }[]>({
    queryKey: ['wonLeads'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/leads?stage=WON`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch WON leads');
      const result = await response.json();
      return result.data || result.leads || [];
    },
    enabled: showAutoCreateModal
  });

  // Fetch subscription plans for auto-create modal
  const { data: subscriptionPlans = [] } = useQuery<{ id: string; name: string; description?: string }[]>({
    queryKey: ['subscriptionPlans'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/subscription-plans`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch subscription plans');
      const result = await response.json();
      return result.data || result.plans || [];
    },
    enabled: showAutoCreateModal
  });

  // Auto-create deliverables mutation
  const autoCreateMutation = useMutation({
    mutationFn: async ({ leadId, planId }: { leadId: string; planId: string }) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/stage-gates/auto-create-deliverables`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ leadId, planId })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to auto-create deliverables');
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      setShowAutoCreateModal(false);
      setAutoCreateLeadId('');
      setAutoCreatePlanId('');
      toast({
        title: 'Success',
        description: `Deliverables auto-created successfully`
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const handleAutoCreate = () => {
    if (!autoCreateLeadId || !autoCreatePlanId) {
      toast({
        title: 'Missing Selection',
        description: 'Please select both a WON lead and a subscription plan',
        variant: 'destructive'
      });
      return;
    }
    autoCreateMutation.mutate({ leadId: autoCreateLeadId, planId: autoCreatePlanId });
  };

  // Filter deliverables by search term
  const filteredDeliverables = useMemo(() => {
    if (!filters.search) return deliverables;

    const searchLower = filters.search.toLowerCase();
    return deliverables.filter(d =>
      d.title.toLowerCase().includes(searchLower) ||
      d.description?.toLowerCase().includes(searchLower) ||
      d.lead?.name.toLowerCase().includes(searchLower) ||
      d.lead?.company.toLowerCase().includes(searchLower) ||
      d.assignedToUser?.name.toLowerCase().includes(searchLower)
    );
  }, [deliverables, filters.search]);

  // Reset page to 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Paginate filtered results
  const totalPages = Math.ceil(filteredDeliverables.length / ITEMS_PER_PAGE);
  const paginatedDeliverables = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredDeliverables.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredDeliverables, currentPage, ITEMS_PER_PAGE]);

  // Calculate overview stats
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const total = deliverables.length;
    const pending = deliverables.filter(d => d.status === 'PENDING' || d.status === 'IN_PROGRESS').length;
    const deliveredThisMonth = deliverables.filter(d => {
      if (!d.deliveredDate) return false;
      const deliveredDate = new Date(d.deliveredDate);
      return deliveredDate.getMonth() === today.getMonth() &&
             deliveredDate.getFullYear() === today.getFullYear();
    }).length;
    const overdue = deliverables.filter(d => {
      if (d.status === 'DELIVERED' || d.status === 'VERIFIED') return false;
      const committedDate = new Date(d.committedDate);
      return committedDate < today;
    }).length;

    return {
      total,
      pending,
      pendingPercentage: total > 0 ? Math.round((pending / total) * 100) : 0,
      deliveredThisMonth,
      overdue
    };
  }, [deliverables]);

  // Calculate days remaining/overdue
  const calculateDaysStatus = (deliverable: Deliverable) => {
    if (deliverable.status === 'DELIVERED' || deliverable.status === 'VERIFIED') {
      return { label: 'Completed', className: 'text-green-600', isOverdue: false };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const committedDate = new Date(deliverable.committedDate);
    committedDate.setHours(0, 0, 0, 0);

    const diffTime = committedDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        label: `${Math.abs(diffDays)} days overdue`,
        className: 'text-red-600 font-semibold',
        isOverdue: true
      };
    } else if (diffDays === 0) {
      return { label: 'Due today', className: 'text-orange-600 font-semibold', isOverdue: false };
    } else if (diffDays === 1) {
      return { label: 'Due tomorrow', className: 'text-yellow-600', isOverdue: false };
    } else {
      return { label: `${diffDays} days left`, className: 'text-gray-600', isOverdue: false };
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this deliverable?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (deliverable: Deliverable) => {
    setEditingDeliverable(deliverable);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingDeliverable(null);
  };

  const toggleSelectId = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedDeliverables.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedDeliverables.map(d => d.id)));
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const handlePDFExport = async (leadId: string) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const resp = await fetch(`${API_BASE_URL}/deliverables/pdf/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('PDF generation failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deliverables-${leadId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export error:', err);
      toast({ title: 'Error', description: 'PDF export failed', variant: 'destructive' });
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Lead Name',
      'Company',
      'Title',
      'Category',
      'Status',
      'Committed Date',
      'Delivered Date',
      'Assigned To',
      'Reminder Sent',
      'Created At'
    ];

    const rows = filteredDeliverables.map(d => [
      d.lead?.name || '',
      d.lead?.company || '',
      d.title,
      d.category,
      d.status,
      new Date(d.committedDate).toLocaleDateString(),
      d.deliveredDate ? new Date(d.deliveredDate).toLocaleDateString() : '',
      d.assignedToUser?.name || 'Unassigned',
      d.reminderSent ? 'Yes' : 'No',
      new Date(d.createdAt).toLocaleDateString()
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deliverables-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Deliverables Dashboard</h1>
            <p className="text-gray-600 mt-1">Track and manage deliverables committed to WON leads</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            {filters.leadId && (
              <button
                onClick={() => handlePDFExport(filters.leadId)}
                className="flex items-center gap-2 px-4 py-2 text-indigo-700 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-50 transition-colors"
              >
                <FileText className="w-4 h-4" />
                Export PDF
              </button>
            )}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAutoCreateModal(true)}
              className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              title="Select a WON lead and subscription plan to auto-generate deliverables from plan templates"
            >
              <Sparkles className="w-5 h-5" />
              Auto-Create from Plan
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Deliverable
            </motion.button>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Deliverables</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Deliverables</p>
                <p className="text-3xl font-bold text-orange-600 mt-2">
                  {stats.pending}
                  <span className="text-lg text-gray-500 ml-2">({stats.pendingPercentage}%)</span>
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Delivered This Month</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{stats.deliveredThisMonth}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overdue Deliverables</p>
                <p className="text-3xl font-bold text-red-600 mt-2">{stats.overdue}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Error State */}
        {deliverablesError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3" role="alert">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Failed to load deliverables</p>
              <p className="text-xs text-red-600 mt-0.5">
                {deliverablesError instanceof Error ? deliverablesError.message : 'Please check your connection and try again.'}
              </p>
            </div>
            <button
              onClick={() => refetchDeliverables()}
              className="px-3 py-1.5 text-sm font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-100 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by title, description, lead..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as DeliverableStatus | 'ALL' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ALL">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="DELIVERED">Delivered</option>
                <option value="VERIFIED">Verified</option>
                <option value="REJECTED">Rejected</option>
                <option value="DELAYED">Delayed</option>
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value as DeliverableCategory | 'ALL' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ALL">All Categories</option>
                <option value="PROPOSAL">Proposal</option>
                <option value="CONTRACT">Contract</option>
                <option value="IMPLEMENTATION">Implementation</option>
                <option value="INSTALLATION">Installation</option>
                <option value="TRAINING">Training</option>
                <option value="SUPPORT">Support</option>
                <option value="DOCUMENTATION">Documentation</option>
                <option value="LICENSE">License</option>
                <option value="SERVICE">Service</option>
                <option value="EQUIPMENT">Equipment</option>
                <option value="CONSULTATION">Consultation</option>
                <option value="MAINTENANCE">Maintenance</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div>
              <button
                onClick={() => setFilters({
                  status: 'ALL',
                  category: 'ALL',
                  assignedTo: '',
                  leadId: '',
                  search: ''
                })}
                className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={paginatedDeliverables.length > 0 && selectedIds.size === paginatedDeliverables.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-indigo-600 rounded border-gray-300"
                    />
                  </th>
                  <th className="px-3 py-3 w-8" />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deliverable</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Committed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivered</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={11} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : paginatedDeliverables.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-6 py-12 text-center">
                      <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      {filters.search || filters.status !== 'ALL' || filters.category !== 'ALL' ? (
                        <>
                          <p className="text-gray-500 font-medium">No deliverables found</p>
                          <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
                        </>
                      ) : (
                        <>
                          <h3 className="text-lg font-medium text-gray-700 mb-2">No deliverables yet</h3>
                          <p className="text-sm text-gray-500 mb-4">
                            Deliverables are work items committed to your WON clients.<br/>
                            Use "Auto-Create from Plan" to generate deliverables from a subscription plan, or add them manually.
                          </p>
                          <button
                            onClick={() => setShowAddModal(true)}
                            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Add Deliverable
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  paginatedDeliverables.map((deliverable) => {
                    const daysStatus = calculateDaysStatus(deliverable);
                    const isExpanded = expandedId === deliverable.id;
                    return (
                      <React.Fragment key={deliverable.id}>
                      <tr className={cn('hover:bg-gray-50 transition-colors', selectedIds.has(deliverable.id) && 'bg-indigo-50')}>
                        <td className="px-3 py-4 w-10">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(deliverable.id)}
                            onChange={() => toggleSelectId(deliverable.id)}
                            className="w-4 h-4 text-indigo-600 rounded border-gray-300"
                          />
                        </td>
                        <td className="px-3 py-4 w-8">
                          <button
                            onClick={() => toggleExpanded(deliverable.id)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Expand details"
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{deliverable.lead?.name}</div>
                            <div className="text-sm text-gray-500">{deliverable.lead?.company}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="max-w-xs">
                            <div className="text-sm font-medium text-gray-900">{deliverable.title}</div>
                            {deliverable.description && (
                              <div className="text-sm text-gray-500 truncate" title={deliverable.description}>
                                {deliverable.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <DeliverableCategoryBadge category={deliverable.category} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <DeliverableStatusBadge status={deliverable.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(deliverable.committedDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {deliverable.deliveredDate
                            ? new Date(deliverable.deliveredDate).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={cn('text-sm font-medium', daysStatus.className)}>
                            {daysStatus.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {deliverable.assignedToUser?.name || 'Unassigned'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            {deliverable.status !== 'DELIVERED' && deliverable.status !== 'VERIFIED' && (
                              <button
                                onClick={() => markDeliveredMutation.mutate(deliverable.id)}
                                className="text-green-600 hover:text-green-900"
                                title="Mark as Delivered"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            {deliverable.status === 'DELIVERED' && (
                              <button
                                onClick={() => markVerifiedMutation.mutate(deliverable.id)}
                                className="text-emerald-600 hover:text-emerald-900"
                                title="Mark as Verified"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleEdit(deliverable)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(deliverable.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${deliverable.id}-detail`}>
                          <td colSpan={11} className="bg-indigo-50 px-6 py-4 border-b border-indigo-100">
                            {/* Tab switcher */}
                            <div className="flex gap-2 mb-3">
                              {(['comments', 'revisions', 'dependencies'] as const).map(tab => (
                                <button
                                  key={tab}
                                  onClick={() => setExpandedTab(tab)}
                                  className={cn(
                                    'px-3 py-1 text-xs font-medium rounded-full transition-colors',
                                    expandedTab === tab
                                      ? 'bg-indigo-600 text-white'
                                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                                  )}
                                >
                                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                              ))}
                            </div>
                            <div className="max-w-2xl">
                              {expandedTab === 'revisions' && (
                                <DeliverableRevisionHistory deliverableId={deliverable.id} />
                              )}
                              {expandedTab === 'dependencies' && (
                                <DeliverableDependencySelector
                                  deliverableId={deliverable.id}
                                  allDeliverables={deliverables.map(d => ({ id: d.id, title: d.title, status: d.status }))}
                                />
                              )}
                              {expandedTab === 'comments' && (
                                <DeliverableCommentThread
                                  deliverableId={deliverable.id}
                                  currentUserId={user?.id || ''}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {filteredDeliverables.length > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>
                {' '}to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * ITEMS_PER_PAGE, filteredDeliverables.length)}
                </span>
                {' '}of{' '}
                <span className="font-medium">{filteredDeliverables.length}</span>
                {' '}results
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg border transition-colors',
                    currentPage === 1
                      ? 'text-gray-400 border-gray-200 bg-gray-50 cursor-not-allowed'
                      : 'text-gray-700 border-gray-300 bg-white hover:bg-gray-50'
                  )}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg border transition-colors',
                    currentPage === totalPages
                      ? 'text-gray-400 border-gray-200 bg-gray-50 cursor-not-allowed'
                      : 'text-gray-700 border-gray-300 bg-white hover:bg-gray-50'
                  )}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      <BulkDeliverableActions
        selectedIds={selectedIds}
        onDeselect={() => setSelectedIds(new Set())}
        onSuccess={() => {
          setSelectedIds(new Set());
          queryClient.invalidateQueries({ queryKey: ['deliverables'] });
        }}
      />

      {/* Add/Edit Modal */}
      {showAddModal && (
        <AddDeliverableModal
          isOpen={showAddModal}
          onClose={handleCloseModal}
          deliverable={editingDeliverable}
        />
      )}

      {/* Auto-Create from Plan Modal */}
      {showAutoCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setShowAutoCreateModal(false);
              setAutoCreateLeadId('');
              setAutoCreatePlanId('');
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Auto-Create Deliverables</h2>
                <p className="text-sm text-gray-500">Create deliverables from a subscription plan</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* WON Lead Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WON Lead</label>
                <select
                  value={autoCreateLeadId}
                  onChange={(e) => setAutoCreateLeadId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a WON lead...</option>
                  {wonLeads.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.name} {lead.company ? `- ${lead.company}` : ''}
                    </option>
                  ))}
                </select>
                {wonLeads.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">No WON leads found</p>
                )}
              </div>

              {/* Subscription Plan Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Plan</label>
                <select
                  value={autoCreatePlanId}
                  onChange={(e) => setAutoCreatePlanId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a plan...</option>
                  {subscriptionPlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </select>
                {subscriptionPlans.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">No subscription plans found</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAutoCreateModal(false);
                  setAutoCreateLeadId('');
                  setAutoCreatePlanId('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAutoCreate}
                disabled={!autoCreateLeadId || !autoCreatePlanId || autoCreateMutation.isPending}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors',
                  !autoCreateLeadId || !autoCreatePlanId || autoCreateMutation.isPending
                    ? 'bg-purple-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700'
                )}
              >
                {autoCreateMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Create Deliverables
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
};
